import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, MapPin, Building, Globe } from 'lucide-react';
import './arrondissement.css';
import { useAuth } from "../AuthContext";
import { useNavigate } from "react-router-dom";
import AxiosInstance from "../AxiosInstance";

const Cities = () => {
  const [cities, setCities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, id: null, name: '' });
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, pages: 0 });

  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [profil, setProfil] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    countryId: '',
    region: ''
  });

  // 🔥 Charger les pays
  const fetchCountries = async () => {
    try {
      const response = await AxiosInstance.get('/api/countries?limit=100');
      if (response.data.success) {
        setCountries(response.data.countries || []);
        // Sélectionner le Cameroun par défaut si disponible
        const cameroun = response.data.countries.find(c => c.code === 'CM');
        if (cameroun && !selectedCountry) {
          setSelectedCountry(cameroun._id);
        }
      }
    } catch (err) {
      console.error('Erreur chargement pays:', err);
    }
  };

  // 🔥 Fonction fetchCities mise à jour
  const fetchCities = async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      });

      if (searchTerm.trim()) params.append('search', searchTerm.trim());
      if (selectedCountry) params.append('countryId', selectedCountry);

      const response = await AxiosInstance.get(`/api/cities?${params}`);
      if (response.data.success) {
        setCities(response.data.cities || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0,
          pages: response.data.pagination?.pages || 0
        }));
      } else {
        throw new Error(response.data.message || 'Erreur lors du chargement des villes');
      }
    } catch (err) {
      setError(err.message);
      setCities([]);
    } finally {
      setLoading(false);
    }
  };

  // 🔥 Charger le profil
  useEffect(() => {
    AxiosInstance.get('/api/users/profile')
      .then(response => setProfil(response.data))
      .catch(console.error);
  }, []);

  // 🔥 Redirection si non authentifié
  useEffect(() => {
    if (!isAuthenticated) navigate("/");
  }, [isAuthenticated, navigate]);

  // 🔥 Charger les pays au montage
  useEffect(() => {
    fetchCountries();
  }, []);

  // 🔥 Debounce pour recherche / pagination / filtre pays
  useEffect(() => {
    if (countries.length > 0) {
      const delayDebounce = setTimeout(() => {
        fetchCities();
      }, 400);
      return () => clearTimeout(delayDebounce);
    }
  }, [pagination.page, pagination.limit, searchTerm, selectedCountry, countries.length]);

  // 🔥 Message de succès temporaire
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 2000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleCountryFilter = (e) => {
    setSelectedCountry(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (e) => {
    const newLimit = Number(e.target.value);
    setPagination(prev => ({
      ...prev,
      limit: newLimit,
      page: 1
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return setError('Le nom de la ville est requis');
    if (!formData.countryId) return setError('Le pays est requis');

    try {
      if (editingItem) {
        await AxiosInstance.put(`/api/cities/${editingItem._id}`, formData);
      } else {
        await AxiosInstance.post('/api/cities', formData);
      }

      await fetchCities();
      resetForm();
      setShowModal(false);
      setSuccessMessage(editingItem ? 'Ville modifiée avec succès ✅' : 'Ville créée avec succès ✅');
    } catch (err) {
      setError(err.message);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      countryId: selectedCountry || '',
      region: ''
    });
    setEditingItem(null);
    setError('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      code: item.code || '',
      countryId: item.countryId?._id || item.countryId || '',
      region: item.region || ''
    });
    setShowModal(true);
  };

  const handleDeleteRequest = (id, name) => setDeleteConfirm({ show: true, id, name });

  const handleDelete = async (id) => {
    try {
      await AxiosInstance.delete(`/api/cities/${id}`);

      await fetchCities();
      setSuccessMessage('Ville supprimée avec succès ✅');
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleteConfirm({ show: false, id: null, name: '' });
    }
  };

  const handleAddNew = () => {
    resetForm();
    setFormData(prev => ({
      ...prev,
      countryId: selectedCountry || (countries[0]?._id || '')
    }));
    setShowModal(true);
  };

  if (loading && countries.length === 0) {
    return <div className="loading-container"><div className="loading-spinner"></div></div>;
  }

  const pageNumbers = Array.from({ length: pagination.pages }, (_, i) => i + 1);

  return (
    <div className="arrondissement-container">
      <div className="container mx-auto px-4 py-4">
        {/* Header */}
        <div className="card page-header">
          <div className="header-content">
            <h1 className="header-title">
              <Building className="inline-block mr-2" size={24} />
              Gestion des Villes
            </h1>
            <button onClick={handleAddNew} className="add-button">
              <Plus className="add-button-icon" /> Nouvelle Ville
            </button>
          </div>
        </div>

        {/* Filtres */}
        <div className="card filters-section">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            {/* Filtre Pays */}
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Globe size={18} /> Filtrer par Pays
              </label>
              <select
                value={selectedCountry}
                onChange={handleCountryFilter}
                className="form-input"
                style={{ width: '100%' }}
              >
                <option value="">Tous les pays</option>
                {countries.map(country => (
                  <option key={country._id} value={country._id}>
                    {country.flag} {country.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Recherche */}
            <div className="search-container" style={{ marginTop: 30, marginBottom: 0 }}>
              <Search className="search-icon" />
              <input
                type="text"
                placeholder="Rechercher une ville par nom ou code..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="search-input"
              />
            </div>
          </div>
        </div>

        {error && <div className="error-message"><strong>Erreur:</strong> {error}</div>}

        {/* Table */}
        <div className="table-container">
          <div className="pagination-controls" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <label>Afficher </label>
              <select value={pagination.limit} onChange={handleLimitChange}>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
                <option value={50}>50</option>
              </select>
              <span> éléments</span>
            </div>
            <div>
              {pagination.total > 0 && (
                <span>
                  Affichage {(pagination.page - 1) * pagination.limit + 1} à {Math.min(pagination.page * pagination.limit, pagination.total)} sur {pagination.total}
                </span>
              )}
            </div>
          </div>

          <div className="table-scroll">
            <table className="data-table">
              <thead className="table-header">
                <tr>
                  <th>Ville</th>
                  <th>Code</th>
                  <th>Région</th>
                  <th>Pays</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="table-body">
                {cities.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="empty-state">
                      <MapPin className="empty-icon" size={48} />
                      <p className="empty-title">
                        {searchTerm || selectedCountry ? 'Aucune ville trouvée' : 'Aucune ville disponible'}
                      </p>
                      <p className="empty-subtitle">
                        {searchTerm || selectedCountry
                          ? 'Essayez de modifier vos critères de recherche'
                          : 'Commencez par ajouter une nouvelle ville'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  cities.map((city) => (
                    <tr key={city._id} className="table-row">
                      <td className="table-cell">
                        <strong>{city.name}</strong>
                      </td>
                      <td className="table-cell">{city.code || '-'}</td>
                      <td className="table-cell">{city.region || '-'}</td>
                      <td className="table-cell">
                        {city.countryId ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ fontSize: '1.2rem' }}>{city.countryId.flag}</span>
                            <span>{city.countryId.name}</span>
                          </span>
                        ) : '-'}
                      </td>
                      <td className="table-cell">
                        <div className="actions-container">
                          <button
                            onClick={() => handleEdit(city)}
                            className="action-button edit"
                            title="Modifier"
                          >
                            <Edit2 className="action-icon" />
                          </button>
                          <button
                            onClick={() => handleDeleteRequest(city._id, city.name)}
                            className="action-button delete"
                            title="Supprimer"
                          >
                            <Trash2 className="action-icon" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="pagination-container" style={{ display: 'flex', justifyContent: 'center', gap: '5px', marginTop: '10px', flexWrap: 'wrap' }}>
              <button
                className="pagination-btn"
                disabled={pagination.page === 1}
                onClick={() => handlePageChange(pagination.page - 1)}
              >
                Précédent
              </button>

              {pageNumbers.map(number => (
                <button
                  key={number}
                  onClick={() => handlePageChange(number)}
                  className={`pagination-btn ${pagination.page === number ? 'active' : ''}`}
                >
                  {number}
                </button>
              ))}

              <button
                className="pagination-btn"
                disabled={pagination.page === pagination.pages}
                onClick={() => handlePageChange(pagination.page + 1)}
              >
                Suivant
              </button>
            </div>
          )}
        </div>

        {/* Modal Ajout/Modification */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h2 className="modal-title">
                  {editingItem ? 'Modifier une ville' : 'Ajouter une ville'}
                </h2>
                <button onClick={handleCloseModal} className="modal-close">
                  <X className="modal-close-icon" />
                </button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  {/* Pays */}
                  <div className="form-group">
                    <label className="form-label">
                      <Globe size={18} className="inline-block mr-1" /> Pays *
                    </label>
                    <select
                      value={formData.countryId}
                      required
                      onChange={(e) => setFormData({ ...formData, countryId: e.target.value })}
                      className="form-input"
                    >
                      <option value="">Sélectionner un pays</option>
                      {countries.map(country => (
                        <option key={country._id} value={country._id}>
                          {country.flag} {country.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Nom */}
                  <div className="form-group">
                    <label className="form-label">Nom de la ville *</label>
                    <input
                      type="text"
                      value={formData.name}
                      required
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="form-input"
                      placeholder="Ex: Douala"
                    />
                  </div>

                  {/* Code */}
                  <div className="form-group">
                    <label className="form-label">Code</label>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                      className="form-input"
                      placeholder="Ex: DLA"
                    />
                  </div>

                  {/* Région */}
                  <div className="form-group">
                    <label className="form-label">Région</label>
                    <input
                      type="text"
                      value={formData.region}
                      onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                      className="form-input"
                      placeholder="Ex: Littoral"
                    />
                  </div>

                  <div className="modal-actions">
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="modal-button cancel"
                    >
                      Annuler
                    </button>
                    <button type="submit" className="modal-button submit">
                      {editingItem ? 'Modifier' : 'Ajouter'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Modal Confirmation Suppression */}
        {deleteConfirm.show && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h2 className="modal-title">Confirmation</h2>
              </div>
              <div className="modal-body">
                <p>
                  Voulez-vous vraiment supprimer la ville de <strong>{deleteConfirm.name}</strong> ?
                </p>
                <div className="modal-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    style={{ backgroundColor: '#6c757d', color: 'white', padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                    onClick={() => setDeleteConfirm({ show: false, id: null, name: '' })}
                  >
                    Annuler
                  </button>
                  <button
                    style={{ backgroundColor: '#dc3545', color: 'white', padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                    onClick={() => handleDelete(deleteConfirm.id)}
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Succès */}
        {successMessage && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h2 className="modal-title">Succès</h2>
              </div>
              <div className="modal-body">
                <p>{successMessage}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cities;